import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import type {
	NewTicketType,
	TicketTypeData,
	TicketTypeResponse,
	TicketType,
} from '../../../common/types.tsx';
import { createTicketTypeService } from '../../../services/create-ticket-type/index.tsx';
import { similarityPercentage } from './utils.tsx';

const createTicketType =
	(
		newTicket: NewTicketType,
		groupIds: number[],
		suggestedName: string,
		suggestedDescription: string,
		isSuggestedByAI: boolean,
		analyticsEvent: UIAnalyticsEvent,
		onTicketCreated: (arg1: number) => void,
	): Action<TicketTypeData> =>
	async ({ getState, setState }) => {
		setState({ pendingTicketTypeCreation: { error: null, data: newTicket } });
		const {
			appProps: { projectId },
		} = getState();

		try {
			const { issueType, iconUrl: url, iconId } = newTicket;
			if (issueType === null || url === null || iconId === null) {
				throw new Error('Missing issuetype or icon data');
			}

			const result: TicketTypeResponse = await createTicketTypeService(
				newTicket,
				projectId,
				groupIds,
			);

			const {
				name,
				id,
				externalId,
				icon,
				iconUrl,
				description,
				usedByEmailSettings,
				helpText,
				groups,
				issueType: { id: issueTypeId },
				practices,
				restrictionStatus,
				issueSecurityStatus,
			} = result;

			const ticketTypeCreated: TicketType = {
				name,
				id,
				iconUrl,
				iconId: icon,
				description,
				usedByEmailSettings,
				helpText,
				groups,
				issueType: Number(issueTypeId),
				practices: practices.map((practice) => practice.key),
				externalId,
				restrictionStatus,
				issueSecurityStatus,
			};

			const { ticketTypes } = getState();
			const ticketTypesData = { ...ticketTypes };
			ticketTypesData[id.toString()] = { currentState: ticketTypeCreated, updateState: {} };
			onTicketCreated(id);
			setState({
				pendingTicketTypeCreation: { error: null, data: null },
				ticketTypes: ticketTypesData,
			});

			fireTrackAnalytics(analyticsEvent, 'requestType created', {
				isDuplicate: false,
				isSuggestedByAI,
				nameSimilarityPercentage: isSuggestedByAI ? similarityPercentage(name, suggestedName) : 0,
				descriptionSimilarityPercentage: isSuggestedByAI
					? similarityPercentage(description, suggestedDescription)
					: 0,
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			setState({
				pendingTicketTypeCreation: { error, data: newTicket },
			});
			fireErrorAnalytics({
				event: analyticsEvent,
				meta: {
					id: 'createTicketTypeService',
					packageName: 'jiraServicedeskTicketTypesStore',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	};

export default createTicketType;
